// src/components/ui/2-organisms/ImageModal/ImageModal.js

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from '../../1-molecules/Icon/Icon';
import styles from './ImageModal.module.css';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';

/**
 * Full-screen image modal with navigation
 * @param {Array} images - Array of all image URLs
 * @param {number} currentIndex - Currently selected image index
 * @param {function} onClose - Function to close the modal
 * @param {function} onPrevious - Function to navigate to previous image
 * @param {function} onNext - Function to navigate to next image
 */
const ImageModal = ({ images, currentIndex, onClose, onPrevious, onNext }) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    // Handle keyboard navigation
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        onPrevious();
      } else if (e.key === 'ArrowRight') {
        onNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onPrevious, onNext]);
  useBodyScrollLock(true);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      onNext();
    }
    if (isRightSwipe) {
      onPrevious();
    }
  };

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  return ReactDOM.createPortal(
    <div className={styles.modal}>
      {/* Header */}
      <div className={styles.header}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="close" size="m" />
        </button>
        <div className={styles.counter}>
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Image container */}
      <div 
        className={styles.imageContainer}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img 
          src={images[currentIndex]} 
          alt={`Image ${currentIndex + 1} of ${images.length}`}
          className={styles.image}
        />

        {/* Navigation buttons for desktop */}
        {hasPrevious && (
          <button
            className={`${styles.navButton} ${styles.prevButton}`}
            onClick={onPrevious}
            aria-label="Previous image"
          >
            <Icon name="chevron-left" size="l" />
          </button>
        )}

        {hasNext && (
          <button
            className={`${styles.navButton} ${styles.nextButton}`}
            onClick={onNext}
            aria-label="Next image"
          >
            <Icon name="chevron-right" size="l" />
          </button>
        )}
      </div>

      {/* Bottom indicator dots */}
      {images.length > 1 && (
        <div className={styles.indicators}>
          {images.map((_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
              onClick={() => {
                // Update the index through parent component
                if (index > currentIndex) {
                  for (let i = currentIndex; i < index; i++) {
                    onNext();
                  }
                } else if (index < currentIndex) {
                  for (let i = currentIndex; i > index; i--) {
                    onPrevious();
                  }
                }
              }}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

export default ImageModal;
